/** Optional client timings from the map summary, in milliseconds. */
export interface MapRecostLatency {
    clientLatencyP50ControlMs?: number | null;
    clientLatencyP50CandidateMs?: number | null;
    clientLatencyP99ControlMs?: number | null;
    clientLatencyP99CandidateMs?: number | null;
    clientLatencyRecordCount?: number | null;
    clientLatencyPairedCount?: number | null;
}

/** Station summary attributes retain the existing Glue column names. */
export interface StationRecostLatency {
    client_latency_p50_control_ms?: number | null;
    client_latency_p50_candidate_ms?: number | null;
    client_latency_p99_control_ms?: number | null;
    client_latency_p99_candidate_ms?: number | null;
    client_latency_record_count?: number | null;
    client_latency_paired_count?: number | null;
}

export interface RecostLatencyChanges {
    latencyP50ChangePercent: number | null;
    latencyP99ChangePercent: number | null;
}

/** Missing or invalid timings stay unknown; a measured zero is valid. */
export function latencyValue(value: unknown): number | null {
    return typeof value === "number" && Number.isFinite(value) && value >= 0 ? value : null;
}

/** Compares the two stored percentiles, not the percentiles of per-trip changes. */
export function latencyChangePercent(control: unknown, candidate: unknown): number | null {
    const controlMs = latencyValue(control);
    const candidateMs = latencyValue(candidate);
    if (controlMs === null || controlMs === 0 || candidateMs === null) {
        return null;
    }
    const change = ((candidateMs - controlMs) / controlMs) * 100;
    return Number.isFinite(change) ? change : null;
}

/** Uses the pooled map percentiles already calculated by MWO. */
export function mapLatencyChanges(row: MapRecostLatency): RecostLatencyChanges {
    return {
        latencyP50ChangePercent: latencyChangePercent(row.clientLatencyP50ControlMs, row.clientLatencyP50CandidateMs),
        latencyP99ChangePercent: latencyChangePercent(row.clientLatencyP99ControlMs, row.clientLatencyP99CandidateMs),
    };
}

/** Uses the station percentiles already calculated by MWO. */
export function stationLatencyChanges(row: StationRecostLatency): RecostLatencyChanges {
    return {
        latencyP50ChangePercent: latencyChangePercent(row.client_latency_p50_control_ms, row.client_latency_p50_candidate_ms),
        latencyP99ChangePercent: latencyChangePercent(row.client_latency_p99_control_ms, row.client_latency_p99_candidate_ms),
    };
}

/** Keeps unavailable values distinct from zero and makes the change direction explicit. */
export function formatLatencyChange(value: number | null | undefined): string {
    if (value == null || !Number.isFinite(value)) {
        return "N/A";
    }
    if (value !== 0 && Math.abs(value) < 0.01) {
        return value > 0 ? "+<0.01%" : "-<0.01%";
    }
    return `${value > 0 ? "+" : ""}${value.toFixed(2)}%`;
}

/** Retains sub-millisecond measurements without displaying small positive timings as zero. */
export function formatLatencyMs(value: unknown): string {
    const milliseconds = latencyValue(value);
    if (milliseconds === null) {
        return "N/A";
    }
    return milliseconds > 0 && milliseconds < 0.01 ? "<0.01 ms" : `${milliseconds.toFixed(2)} ms`;
}
